import React, { useState, useEffect } from 'react';
import { FaPlus, FaUsers } from 'react-icons/fa';
import {
  Container,
  CreateButton,
  JobPostsGrid,
  EmptyStateContainer,
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  CloseButton,
  FormActions,
  CancelButton,
  SubmitButton
} from '../../../styles/components/JobPostManagement';
import {
  JobPostCard,
  SearchFilters,
  JobPostForm,
  ApplicationsView
} from './components';
import { PageHeader, PageTitle } from '../../../styles/components';
import { useNotifications } from '../../../contexts/notification';

// Mock data for company job posts
const mockJobPosts = [
  {
    id: 1,
    title: "Frontend Developer Intern",
    duration: "3 months",
    paid: true,
    salary: "$1200/month",
    location: "New York, NY",
    skills: ["JavaScript", "React", "HTML", "CSS"],
    description: "Join our development team to create responsive web applications using modern JavaScript frameworks.",
    applications: 12,
    status: "active",
    createdAt: "2025-04-01"
  },
  {
    id: 2,
    title: "Backend Developer Intern",
    duration: "4 months",
    paid: true,
    salary: "$1300/month",
    location: "San Francisco, CA",
    skills: ["Node.js", "Express", "MongoDB", "API Development"],
    description: "Work with our backend team to develop and maintain APIs and server-side applications.",
    applications: 8,
    status: "active",
    createdAt: "2025-04-05"
  },
  {
    id: 3,
    title: "UI/UX Design Intern",
    duration: "6 months",
    paid: true,
    salary: "$1100/month",
    location: "Remote",
    skills: ["Figma", "Adobe XD", "UI Design", "User Research"],
    description: "Help design intuitive user interfaces and experiences for our products.",
    applications: 15,
    status: "active",
    createdAt: "2025-04-10"
  }
];

// Function to generate dummy applications data based on a count
const generateDummyApplications = (jobId, jobTitle, count) => {
  const universities = ["Stanford University", "MIT", "Harvard University", "UC Berkeley", "Princeton", "Cornell", "Georgia Tech", "UCLA", "University of Michigan", "Columbia University"];
  const majors = ["Computer Science", "Computer Engineering", "Information Technology", "Software Engineering", "Data Science", "Information Systems", "Electrical Engineering", "Mechanical Engineering", "Industrial Engineering", "Design"];
  const skills = ["JavaScript", "React", "HTML", "CSS", "Node.js", "Python", "Java", "C++", "SQL", "UI/UX Design", "Figma", "Adobe XD", "MongoDB", "AWS", "Docker", "Git", "TypeScript", "PHP", "Ruby", "Swift"];
  const statuses = ["pending", "reviewing", "interview", "accepted", "rejected"];
  const firstNames = ["John", "Sarah", "Michael", "Emma", "David", "Jennifer", "James", "Mary", "Robert", "Emily", "Andrew", "Jessica", "Daniel", "Catherine", "Christopher", "Kate"];
  const lastNames = ["Smith", "Johnson", "Brown", "Wilson", "Jones", "Taylor", "Davis", "Miller", "Williams", "Anderson", "Thomas", "Jackson", "White", "Harris", "Martin", "Thompson"];
  
  const applications = [];
  
  for (let i = 0; i < count; i++) {
    const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
    const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];
    
    const randSkills = [];
    const numSkills = Math.floor(Math.random() * 5) + 2; // 2-6 skills
    for (let j = 0; j < numSkills; j++) {
      const skill = skills[Math.floor(Math.random() * skills.length)];
      if (!randSkills.includes(skill)) {
        randSkills.push(skill);
      }
    }
    
    // Randomly assign some applications as current interns or completed interns
    let appStatus = statuses[Math.floor(Math.random() * statuses.length)];
    let startDate, endDate;
    
    // Give about 15% of applications "current intern" status and 10% "internship complete" status
    const randomStatus = Math.random();
    if (randomStatus < 0.15) {
      appStatus = "current intern";
      startDate = new Date(new Date().getTime() - Math.floor(Math.random() * 60) * 24 * 60 * 60 * 1000).toISOString().split('T')[0]; // Random start date up to 60 days ago
      endDate = new Date(new Date().getTime() + Math.floor(Math.random() * 90) * 24 * 60 * 60 * 1000).toISOString().split('T')[0]; // Random end date up to 90 days in future
    } else if (randomStatus < 0.25) {
      appStatus = "internship complete";
      startDate = new Date(new Date().getTime() - Math.floor(Math.random() * 120) * 24 * 60 * 60 * 1000).toISOString().split('T')[0]; // Random start date up to 120 days ago
      endDate = new Date(new Date().getTime() - Math.floor(Math.random() * 30) * 24 * 60 * 60 * 1000).toISOString().split('T')[0]; // Random end date up to 30 days ago
    }
    
    applications.push({
      id: `app-${jobId}-${i+1}`,
      jobId,
      jobTitle,
      studentName: `${firstName} ${lastName}`,
      studentEmail: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@example.com`,
      studentPhone: `(${Math.floor(Math.random() * 900) + 100}) ${Math.floor(Math.random() * 900) + 100}-${Math.floor(Math.random() * 9000) + 1000}`,
      studentUniversity: universities[Math.floor(Math.random() * universities.length)],
      studentMajor: majors[Math.floor(Math.random() * majors.length)],
      studentGPA: (Math.random() * 1 + 3).toFixed(2), // GPA between 3.0 and 4.0
      studentSkills: randSkills,
      resumeUrl: "#",
      coverLetterUrl: "#",
      appliedDate: new Date(new Date().getTime() - Math.floor(Math.random() * 30) * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Random date within last 30 days
      status: appStatus,
      startDate,
      endDate
    });
  }
  
  return applications;
};

const JobPostManagement = () => {
  const { addNotification } = useNotifications();
  const [jobPosts, setJobPosts] = useState(mockJobPosts);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    showActive: true,
    showPending: true,
    showExpired: false,
    showPaid: false
  });
  
  // Filtered job posts based on search and filters
  const [filteredJobPosts, setFilteredJobPosts] = useState([]);
  
  // Job post form state
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    duration: '',
    paid: false,
    salary: '',
    location: '',
    skills: [],
    description: '',
    status: 'pending'
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedPostId, setSelectedPostId] = useState(null);
  
  // Applications view state
  const [showApplications, setShowApplications] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [applications, setApplications] = useState([]);
  
  // Confirmation modal state
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [postToDelete, setPostToDelete] = useState(null);
  
  useEffect(() => {
    let filtered = [...jobPosts];
    
    // Apply search query
    if (searchQuery) {
      filtered = filtered.filter(post => 
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.skills.some(skill => skill.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }
    
    // Apply status filters
    if (!filters.showActive && !filters.showPending && !filters.showExpired) {
      // If no filters are selected, show all
    } else {
      filtered = filtered.filter(post => 
        (filters.showActive && post.status === 'active') ||
        (filters.showPending && post.status === 'pending') ||
        (filters.showExpired && post.status === 'expired')
      );
    }
    
    // Apply paid filter
    if (filters.showPaid) {
      filtered = filtered.filter(post => post.paid);
    }
    
    setFilteredJobPosts(filtered);
  }, [jobPosts, searchQuery, filters]);
  
  // Open job post form for creating a new post
  const handleCreateJobPost = () => {
    setFormData({
      title: '',
      duration: '',
      paid: false,
      salary: '',
      location: '',
      skills: [],
      description: '',
      status: 'pending'
    });
    setIsEditMode(false);
    setSelectedPostId(null);
    setShowForm(true);
  };
  
  // Open job post form for editing an existing post
  const handleEditJobPost = (post) => {
    setFormData({
      title: post.title,
      duration: post.duration,
      paid: post.paid,
      salary: post.salary || '',
      location: post.location,
      skills: post.skills,
      description: post.description,
      status: post.status
    });
    setIsEditMode(true);
    setSelectedPostId(post.id);
    setShowForm(true);
  };
  
  // Submit job post form (create or update)
  const handleSubmitJobPost = (data) => {
    if (isEditMode) {
      // Update existing job post
      setJobPosts(prevPosts =>
        prevPosts.map(post =>
          post.id === selectedPostId
            ? {
                ...post,
                title: data.title,
                duration: data.duration,
                paid: data.paid,
                salary: data.paid ? data.salary : null,
                location: data.location,
                skills: data.skills,
                description: data.description,
                status: data.status
              }
            : post
        )
      );
    } else {
      // Create new job post
      const newPost = {
        id: Date.now(),
        title: data.title,
        duration: data.duration,
        paid: data.paid,
        salary: data.paid ? data.salary : null,
        location: data.location,
        skills: data.skills,
        description: data.description,
        applications: 0,
        status: data.status,
        createdAt: new Date().toISOString().split('T')[0]
      };
      
      setJobPosts(prevPosts => [...prevPosts, newPost]);
    }
    
    // Close the form
    setShowForm(false);
  };
  
  // Delete a job post - shows confirmation modal
  const handleDeleteJobPost = (postId) => {
    // Find the post to get its title for the confirmation message
    const post = jobPosts.find(post => post.id === postId);
    if (!post) return;

    // Show confirmation modal by setting state
    setPostToDelete(post);
    setShowDeleteConfirmation(true);
  };
  
  // Confirm deletion and actually delete the job post
  const confirmDeleteJobPost = () => {
    if (!postToDelete) return;
    
    // Proceed with deletion
    setJobPosts(prevPosts => prevPosts.filter(post => post.id !== postToDelete.id));
    
    // Show success notification
    addNotification({
      title: "Job Post Deleted",
      message: `The job post "${postToDelete.title}" has been successfully deleted.`,
      type: "success"
    });
    
    // Close the confirmation modal
    setShowDeleteConfirmation(false);
    setPostToDelete(null);
  };
  
  // Handle viewing applications for a job
  const handleViewApplications = (post) => {
    const applicationData = generateDummyApplications(post.id, post.title, post.applications);
    setSelectedPost(post);
    setApplications(applicationData);
    setShowApplications(true);
  };
  
  // Get status display text and color based on status
  const getStatusDisplay = (status) => {
    switch (status) {
      case 'active':
        return { text: 'Active', color: 'rgba(16, 185, 129, 0.8)' };
      case 'pending':
        return { text: 'Pending', color: 'rgba(245, 158, 11, 0.8)' };
      case 'expired':
        return { text: 'Expired', color: 'rgba(239, 68, 68, 0.8)' };
      case 'reviewing':
        return { text: 'Reviewing', color: 'rgba(59, 130, 246, 0.8)' };
      case 'interview':
        return { text: 'Interview', color: 'rgba(139, 92, 246, 0.8)' };
      case 'accepted':
        return { text: 'Accepted', color: 'rgba(16, 185, 129, 0.8)' };
      case 'rejected':
        return { text: 'Rejected', color: 'rgba(239, 68, 68, 0.8)' };
      case 'current intern':
        return { text: 'Current Intern', color: 'rgba(5, 150, 105, 0.8)' };
      case 'internship complete':
        return { text: 'Internship Complete', color: 'rgba(79, 70, 229, 0.8)' };
      default:
        return { text: 'Unknown', color: 'rgba(156, 163, 175, 0.8)' };
    }
  };
  
  return (
    <div className="job-post-management">
      <Container>
        <PageHeader>
          <PageTitle>Job Post Management</PageTitle>
          <CreateButton onClick={handleCreateJobPost}>
            <FaPlus /> Create New Job Post
          </CreateButton>
        </PageHeader>
        
        {/* Search and Filters */}
        <SearchFilters 
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          filters={filters}
          setFilters={setFilters}
        />
        
        {/* Job Posts Grid */}
        <JobPostsGrid>
          {filteredJobPosts.length === 0 ? (
            <EmptyStateContainer style={{ gridColumn: '1 / -1' }}>
              <FaUsers />
              <h3>No job posts found</h3>
              <p>Create a new job post to start receiving applications from talented students.</p>
              <CreateButton onClick={handleCreateJobPost}>
                <FaPlus /> Create New Job Post
              </CreateButton>
            </EmptyStateContainer>
          ) : (
            filteredJobPosts.map(post => (
              <JobPostCard
                key={post.id}
                post={post}
                getStatusDisplay={getStatusDisplay}
                onEdit={handleEditJobPost}
                onDelete={handleDeleteJobPost}
                onViewApplications={handleViewApplications}
              />
            ))
          )}
        </JobPostsGrid>
        
        {/* Job Post Form Modal */}
        {showForm && (
          <ModalOverlay>
            <JobPostForm
              postData={formData}
              isEdit={isEditMode}
              onSubmit={handleSubmitJobPost}
              onCancel={() => setShowForm(false)}
            />
          </ModalOverlay>
        )}
        
        {/* Applications Modal */}
        {showApplications && selectedPost && (
          <ApplicationsView
            jobPost={selectedPost}
            applications={applications}
            onClose={() => setShowApplications(false)}
            getStatusDisplay={getStatusDisplay}
          />
        )}
        
        {/* Delete Confirmation Modal */}
        {showDeleteConfirmation && postToDelete && (
          <ModalOverlay>
            <ModalContainer style={{ maxWidth: '500px' }}>
              <ModalHeader>
                <ModalTitle>Confirm Deletion</ModalTitle>
                <CloseButton onClick={() => {
                  setShowDeleteConfirmation(false);
                  setPostToDelete(null);
                }}>
                  &times;
                </CloseButton>
              </ModalHeader>
              
              <p style={{ 
                color: 'rgba(255, 255, 255, 0.9)', 
                marginBottom: '1.5rem'
              }}>
                Are you sure you want to delete the job post "{postToDelete.title}"? 
                This action cannot be undone.
              </p>
              
              <FormActions>
                <CancelButton 
                  onClick={() => {
                    setShowDeleteConfirmation(false);
                    setPostToDelete(null);
                  }}
                >
                  Cancel
                </CancelButton>
                <SubmitButton 
                  onClick={confirmDeleteJobPost}
                  style={{ background: 'rgba(239, 68, 68, 0.8)' }}
                >
                  Delete
                </SubmitButton>
              </FormActions>
            </ModalContainer>
          </ModalOverlay>
        )}
      </Container>
    </div>
  );
};

export default JobPostManagement;
